import Button from '@/shared/Button'

import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'

import CloseIcon from '@mui/icons-material/Close'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import VideoUpload from './VideoUpload'

import FormCreator from '@/shared/FormCreator'
import omit from 'lodash/omit'
import set from 'lodash/set'
import isEmpty from 'lodash/isEmpty'
import Stepper from '../Stepper'

import * as Yup from 'yup'

const mapStateToProps = ({ loading }) => ({
  isUploading: loading['userGroup/uploadFile'],
  isChecking: loading['userGroup/checkFile'],
  isFinalizing: loading['userGroup/uploadFileFinal'],
})

const inputConfig = {
  variant: 'standard',
  inputBaseProps: {
    labelSuffix: '',
    flexDirection: 'column',
    alignItems: 'stretch',
    spacing: 0,
    labelProps: { sx: { color: theme => theme.palette.btn_positive.main } },
  },
  color: 'btn_positive',
  endAdornment: args => {
    const {
      value,
      inputProps: { maxLength },
    } = args

    return (
      <Typography
        variant="caption"
        sx={{ color: '#00000040', fontSize: '14px !important' }}
      >{`${value.length}/${maxLength}`}</Typography>
    )
  },
}

const CreateCourseDialog = ({
  dispatch,
  isUploading,
  isChecking,
  isFinalizing,
  onClose,
  onComplete,
  data,
  ...props
}) => {
  const form = useRef()
  const step1Data = useRef()
  const newCategory = useRef()
  const selectedCategory = useRef()

  const [step, setStep] = useState(0)
  const [stepNext, setStepNext] = useState([false, false])

  const { t } = useTranslation()

  const handleClose = () => {
    onClose?.()
  }

  const resetState = () => {
    setStep(0)
    setStepNext([false, false])
    step1Data.current = null
    form.current?.reset?.()
  }

  const isLoading = isUploading || isChecking || isFinalizing

  const steps = [
    {
      label: t('general.select_upload_video'),
      icon: <SystemUpdateAltIcon sx={{ transform: 'rotate(180deg)' }} />,
    },
    { label: t('general.fill_details'), icon: <DescriptionOutlinedIcon /> },
  ]

  const createCourseTemplate = payload => {
    dispatch({
      type: 'courseTemplate/createCourseTemplate',
      payload,
      successCallback: () => {
        dispatch({
          type: 'app/notify',
          payload: { message: t('messages.video_created'), severity: 'success' },
        })

        onComplete?.()
        resetState()
        handleClose()
      },
    })
  }

  const handleSubmit = () => {
    const { handleSubmit: formSubmit } = form.current

    formSubmit?.(formValues => {
      const {
        vidUpId: videoUploadUrlId,
        thumbUpId: thumbNailUploadUrlId,
        videoLength,
      } = step1Data.current || {}

      const payload = {
        ...formValues,
        videoUploadUrlId,
        thumbNailUploadUrlId,
        videoLength,
        courseLabelList: [],
      }

      if (!isEmpty(selectedCategory.current)) {
        payload.courseLabelList = [selectedCategory.current]
        createCourseTemplate(payload)
      } else if (!isEmpty(newCategory.current)) {
        dispatch({
          type: 'courseTemplate/createCourseTemplateLabel',
          payload: { name: newCategory.current },
          successCallback: result => {
            const { name } = result
            payload.courseLabelList = [name]
            createCourseTemplate(payload)
          },
        })
      } else {
        createCourseTemplate(payload)
      }
    })?.()
  }

  const setStepNextByIndex = (index, val) => {
    const newStepNext = [...stepNext]
    set(newStepNext, `[${index}]`, val)
    setStepNext(newStepNext)
  }

  const onNext = () => {
    switch (step) {
      case 0: {
        setStep(step + 1)
        break
      }
      case 1: {
        handleSubmit()
        break
      }
      default:
        break
    }
  }

  const renderStepComponent = () => {
    switch (step) {
      case 0:
        return (
          <>
            <Typography fontWeight={'bold'} color={theme => theme.palette.secondary_80.main}>
              {t('general.upload_video_add_label')}
            </Typography>
            <Typography sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '12px' }}>
              {t('general.upload_video_description_part_one')}
              <br />
              {t('general.upload_video_description_part_two')}
            </Typography>
            <VideoUpload
              dispatch={dispatch}
              setCategory={value => {
                selectedCategory.current = value
              }}
              setNewCategory={value => {
                newCategory.current = value
              }}
              onComplete={values => {
                const { vidUpId, thumbUpId, videoLength } = values

                step1Data.current = { vidUpId, thumbUpId, videoLength }
                setStepNextByIndex(step, true)
              }}
              onCancel={() => {
                step1Data.current = null

                setStepNextByIndex(step, false)
              }}
            />
          </>
        )
      case 1:
        return (
          <>
            <FormCreator
              ref={form}
              onValidChange={valid => {
                setStepNextByIndex(step, valid)
              }}
              gridItemProps={{ md: 3 }}
              formItems={[
                {
                  label: t('general.video_name'),
                  name: 'courseTempName',
                  validator: Yup.string().required(t('validation.video_name_required')),
                  defaultValue: '',
                  inputConfig: {
                    ...inputConfig,
                    inputProps: { maxLength: 40 },
                    inputBaseProps: {
                      ...inputConfig.inputBaseProps,
                      labelSuffix: (
                        <Typography
                          sx={{ color: theme => theme.palette.primary.main, ml: 1 }}
                        >{`(${t('general.required')})`}</Typography>
                      ),
                    },
                  },
                },
                {
                  label: t('general.course_intro'),
                  name: 'courseTempDesc',
                  defaultValue: '',
                  inputConfig: {
                    ...omit(inputConfig, ['endAdornment']),
                    variant: 'outlined',
                    rows: 5,
                    multiline: true,
                  },
                },
              ]}
              submitComponent={() => null}
              resetComponent={() => null}
            />
          </>
        )
      default:
        return null
    }
  }

  return (
    <Dialog
      {...props}
      maxWidth={'md'}
      sx={{
        '& .MuiDialog-paper': {
          padding: '24px 48px',
          maxWidth: 'unset',
        },
      }}
    >
      <DialogTitle>
        <Stepper step={step} steps={steps} />
        <IconButton
          edge="end"
          color="#979797"
          onClick={() => {
            resetState()
            handleClose()
          }}
          aria-label="close"
          sx={{
            position: 'absolute',
            right: '24px',
            top: '12px',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ maxWidth: { xs: 800, lg: 'unset' }, width: 1000 }}>
        {renderStepComponent()}
      </DialogContent>

      <DialogActions>
        <Box sx={{ marginLeft: 'auto' }}>
          <Button
            sx={{ minWidth: '150px', marginRight: '30px' }}
            loading={isLoading}
            disabled={isLoading}
            onClick={() => {
              if (step === 0) {
                handleClose?.()
                resetState()
              } else {
                setStep(step - 1)
              }
            }}
          >
            {step !== 0 ? t('general.prev_step') : t('general.cancel')}
          </Button>
          <Button
            sx={{ minWidth: '150px' }}
            loading={isLoading}
            disabled={isLoading || !stepNext[step]}
            onClick={onNext}
          >
            {step !== steps.length - 1 ? t('general.next_step') : t('general.confirm_upload')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(CreateCourseDialog)
